import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { TrendingUp, Mail, Lock, Loader2 } from 'lucide-react';

export default function Login() {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(loginData.email, loginData.password);
      toast.success('Bienvenido a FinScope');
      
      // Redirect based on role
      if (userData.role === 'super_admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container bg-background">
      {/* Form Section */}
      <div className="login-form-section">
        <div className="w-full max-w-md animate-slideUp">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                FinScope
              </h1>
              <p className="text-sm text-muted-foreground">Inteligencia Financiera</p>
            </div>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Bienvenido
              </CardTitle>
              <CardDescription>
                Accede a tu plataforma de control financiero
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Correo electrónico</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="tu@email.com"
                      className="pl-10"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Iniciar Sesión
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="text-center mt-6 space-y-2">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} FinScope. Plataforma de inteligencia financiera.
            </p>
            <p className="text-xs">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors underline underline-offset-2" data-testid="login-privacy-link">
                Política de Privacidad
              </Link>
              <span className="text-muted-foreground mx-2">·</span>
              <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors underline underline-offset-2" data-testid="login-terms-link">
                Términos y Condiciones
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div 
        className="login-hero-section relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBmaW5hbmNlJTIwb2ZmaWNlJTIwZXhlY3V0aXZlJTIwbWVldGluZ3xlbnwwfHx8fDE3NzI2NjYzMDB8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary/40"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white max-w-lg">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Transforma tus datos en decisiones
            </h2>
            <p className="text-lg opacity-90 mb-8">
              Visualiza tu información financiera, controla gastos, gestiona pagos y toma decisiones estratégicas con claridad.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold">12+</div>
                <div className="text-sm opacity-80">Módulos integrados</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold">100%</div>
                <div className="text-sm opacity-80">Control financiero</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
